/**
 * De tekenaar voor het Krabbelgotchi-beestje, alleen voor de mod.
 *
 * Het beestje is opgebouwd uit zeventien losse stukjes van 16 bij 16: een ei, drie
 * lijfjes, twee paar ogen, vier monden en zeven objecten. Hier teken je ze met de
 * hand; de app zet ze op hun plek. Zie STUKKEN in lib/tamagotchi.js voor de lijst
 * en ankersVan voor waar ze landen.
 *
 * Wat niet getekend is, tekent de code zelf. Je kunt er dus één beginnen en de
 * rest laten staan, en je ziet meteen het verschil.
 */

import { api, asJson } from '../lib/api.js';
import { PB_URL } from '../lib/config.js';
import { playBlip, playPop } from '../lib/sound.js';
import { iconStroke } from '../lib/icons.js';
import { PALETTE_HEX } from '../lib/palette.js';
import * as tam from '../lib/tamagotchi.js';

/**
 * De ontwerpen staan in hun eigen collectie.
 *
 * Ze woonden eerst in `paal_beestje`, tussen de levende beestjes, omdat die tabel
 * ooit hernoemd werd naar de naam die de tekenaar gebruikte. Dat werkte, maar het
 * waren twee verschillende dingen in één lade: een regel per persoon naast een regel
 * per tekening, en aan beide kanten moest je filteren om de ander niet te raken.
 *
 * De verhuizing is op 4 september 2026 gebeurd: de zes ontwerpen die er stonden zijn
 * overgezet en nagemeten, en de velden `sleutel` en `pixels` zijn daarna uit
 * `paal_beestje` gehaald. Er is dus geen terugvalpad meer nodig.
 *
 * Hier stond dat een filter op `sleutel` een 400 geeft. Dat gold voor de oude
 * collectie, waar het veld weg is; op `paal_beestje_ontwerp` werkt filteren gewoon
 * (nagemeten op 7 september 2026: `?filter=(sleutel~'6:')` geeft een 200).
 *
 * De naam houdt het verband met de beestjes vast: `paal_beestje_ontwerp` staat naast
 * `paal_beestje` in de lijst, en dan zie je in één oogopslag dat ze bij elkaar horen.
 */
const COL = 'paal_beestje_ontwerp';

export default {
  /** Het scherm open, en meteen ophalen wat er al getekend is. */
  openBeestje() {
    /**
     * Iedereen mag zijn eigen beestje ontwerpen.
     *
     * Hier stond de betapoort, van toen de vijf soorten samen werden getekend: één
     * oog bijwerken keek alle beestjes van dat soort anders aan, en dat laat je niet
     * aan iedereen. Nu heeft elk beestje zijn eigen tekening en raak je met een oog
     * alleen je eigen beestje. Alleen de spullenbak is nog voor de mod en de testers,
     * want een appeltje is van iedereen; zie beestjeSlots hieronder.
     *
     * Wel een account nodig: je tekening staat onder je accountId op de server, en
     * zonder id is er geen sleutel om hem onder te bewaren. Dat vraagt het scherm
     * zelf, op het moment dat je het opendoet, zoals overal in deze app.
     */
    if (!this.ensureName('Log in of maak een account aan om je eigen beestje te ontwerpen.')) return;
    // Wie het scherm sluit hoort terug te komen waar hij vandaan kwam.
    this.beestjeUitMod = this.state.dialog === 'mod';
    // Je eigen beestje moet in beeld zijn, want zijn naam staat op de tegel en zijn
    // soort bepaalt waar hij op terugvalt. Wie de Krabbelgotchi nog nooit opendeed
    // heeft hem niet in de staat staan; dan komt hij uit de opslag van deze browser,
    // en `tamHaal` haalt daarna de versie van de server erover. Dat leest vooral; het
    // schrijft alleen terug wat deze browser wist en de server nog niet.
    const beest = this.state.tamBeest || this.tamLees() || null;
    // Je begint bij het overzicht met de beestjes, niet midden in een tekening.
    return this.setState({
      dialog: 'beestje', notifOpen: false, beestjeFout: '', beestjeSlot: 0,
      tamBeest: beest,
      // Geen merkjes van de vorige keer. `haalBeestjes` zet de tekeningen terug naar
      // wat er op de server staat, en een merkje dat bleef hangen zei dan "onbewaard"
      // over een doekje dat al lang weer de standaard was; bewaren legde die standaard
      // dan vast als jouw eigen tekening, die daarna niet meer meeliep.
      beestjeVuil: {}
    }, () => { this.haalBeestjes(); this.tamHaal(); });
  },

  /**
   * Jouw beestje of de spullenbak onder handen nemen.
   *
   * `n` is `'eigen'` of `OBJECT_SLOT`. Hier stond `n >= 1 && n <= OBJECT_SLOT`, van
   * toen je uit vijf genummerde beestjes koos; `'eigen' >= 1` is onwaar, dus die poort
   * hield je eigen beestje buiten en klikken op de tegel deed helemaal niets.
   */
  kiesSlot(n) {
    if (n !== 'eigen' && n !== tam.OBJECT_SLOT) return;
    /**
     * De spullenbak is voor de mod en de testers, en dat wordt hier ook gecontroleerd.
     *
     * De tegel staat er al niet zonder vinkje, maar een functie die alleen op een
     * verborgen tegel vertrouwt is geen slot: dat is precies de regel die `openBeestje`
     * en `bewaarStuk` hier ook aanhouden. Zonder deze regel kon je op de objectenpagina
     * belanden, er in tekenen, en dan pas bij het bewaren te horen krijgen dat het niet
     * mag. Objecten zijn van iedereen samen: één appeltje voor alle beestjes.
     */
    if (n === tam.OBJECT_SLOT && !this.magBeta()) {
      // Niet `betaAf`: die zegt "staat nog in beta", en dat is de reden niet. Een
      // appeltje is er één voor alle beestjes samen, dus dit is geen tekening van jou
      // maar een besluit voor iedereen.
      this.notify('Het appeltje, het hartje en de bal zijn van iedereen samen. Die houden de mod en de testers bij.');
      return;
    }
    // Welke pagina je krijgt volgt uit het vak: jouw beestje krijgt de beestjespagina,
    // de spullenbak die van de objecten. Vandaar dat er geen tabbladen meer zijn.
    const groep = n === tam.OBJECT_SLOT ? 'object' : 'beestje';
    // Een schone lei: terugstappen naar de tekening van een ánder beestje is geen
    // stap terug maar een verrassing.
    this.beestjeTerugStapel = [];
    this.beestjeVooruitStapel = [];
    this.setState({
      beestjeSlot: n, beestjeGroep: groep, beestjeAan: '',
      beestjeTerugKan: false, beestjeVooruitKan: false
    }, () => this.vulGroep(groep));
  },

  /** Terug naar de zes. */
  naarSlots() {
    // Ook de stapel leeg: op het overzicht is er niets om ongedaan te maken, en
    // Cmd+Z daar zou een beestje veranderen waar je niet naar kijkt.
    this.beestjeTerugStapel = [];
    this.beestjeVooruitStapel = [];
    return this.setState({
      beestjeSlot: 0, beestjeFout: '', beestjeTerugKan: false, beestjeVooruitKan: false
    });
  },

  /**
   * Wiens tekening je nu aan het bewerken bent.
   *
   * `'eigen'` is jij; de spullenbak is `OBJECT_SLOT`. Op het overzicht (`0`) is er
   * niets in bewerking en valt hij terug op jou, zodat de voorbeeldtegels je eigen
   * beestje laten zien.
   */
  beestjeEigenaar() {
    if (this.state.beestjeSlot === tam.OBJECT_SLOT) return tam.OBJECT_SLOT;
    return this.state.accountId || '';
  },

  /** De volledige sleutel van het stukje waar je nu aan werkt. */
  stukKey(sleutel) {
    return tam.stukSleutel(this.beestjeEigenaar(), sleutel || this.state.beestjeAan);
  },

  /**
   * Weg met dit scherm.
   *
   * Terug naar het modgereedschap als je daar vandaan kwam; kwam je uit het startmenu,
   * dan is er niets om naar terug te gaan. Zonder dat onderscheid belandde een tester
   * op een modscherm dat hij niet mag openen.
   */
  sluitBeestje() {
    return this.setState({ dialog: this.beestjeUitMod ? 'mod' : null, beestjeFout: '' });
  },

  /**
   * Alle stukjes ophalen.
   *
   * Ook buiten dit scherm nodig: het spel zelf tekent ermee. Vandaar dat de
   * uitkomst in `beestjes` blijft staan als een kaart van sleutel naar tekenreeks,
   * klaar voor gebruik.
   */
  async haalBeestjes() {
    if (!PB_URL) return;
    try {
      /**
       * Alle pagina's, niet alleen de eerste.
       *
       * Hier stond `perPage=200` en één verzoek. Dat was ruim toen de tekeningen van
       * iedereen samen waren: vijf soorten van tien stukjes plus zeven objecten is
       * zevenenvijftig regels, en meer konden er niet zijn. Sinds elk beestje zijn
       * eigen tekening heeft groeit het mee met het aantal collega's: tien regels per
       * persoon. Bij twintig mensen zit je al aan tweehonderd, en dan viel de rest er
       * stil af — met als gevolg dat het beestje van de een het wel had en van de
       * ander niet, zonder foutmelding.
       *
       * Vijfhonderd is het maximum dat PocketBase per pagina geeft, dus de lus is er
       * voor daarboven. Bij zeven regels is dat één verzoek, net als eerst.
       */
      const rijen = [];
      let bladzijde = 1;
      let bladzijden = 1;
      let mislukt = false;
      let fout = 0;
      while (bladzijde <= bladzijden && bladzijde <= 10) {
        const res = await api(`${PB_URL}/api/collections/${COL}/records`
          + `?perPage=500&page=${bladzijde}&fields=id,sleutel,pixels`);
        if (!res.ok) { mislukt = true; fout = res.status; break; }
        const blad = await res.json();
        (blad.items || []).forEach((r) => rijen.push(r));
        bladzijden = Number(blad.totalPages) || 1;
        bladzijde += 1;
      }
      const nieuweRijen = mislukt && !rijen.length ? null : rijen;
      const kaart = {};
      const ids = {};
      const eigen = {};
      rijen.forEach((r) => {
        if (!tam.stukOk(r.sleutel)) return;
        // Wat op de vorige maat bewaard is een ring kleiner knippen, anders telt het
        // niet meer als doekje en was je tekening stil weg. Zie krimpStuk.
        const stuk = tam.stukVan(String(r.sleutel).split(':')[1]);
        kaart[r.sleutel] = tam.krimpStuk(r.pixels, stuk) || r.pixels || '';
        ids[r.sleutel] = r.id;
        if (/[0-7]/.test(kaart[r.sleutel])) eigen[r.sleutel] = true;
      });
      /**
       * Objecten uit de tijd dat elk beestje zijn eigen spullen had.
       *
       * Die staan als `1:bal` op de server en horen nu in de spullenbak. Ze worden
       * overgenomen inclusief hun regelnummer, dus zodra je zo'n object opslaat wordt
       * diezelfde regel omgedoopt en blijft er geen tweede achter. stukOk keurt de
       * oude vorm af, dus dit moet vóór die keuring gebeuren; vandaar de losse ronde.
       */
      rijen.forEach((r) => {
        const kaal = String(r.sleutel || '').split(':');
        const stuk = kaal.length === 2 && tam.stukVan(kaal[1]);
        if (!stuk || stuk.groep !== 'object' || Number(kaal[0]) === tam.OBJECT_SLOT) return;
        const nieuweSleutel = tam.OBJECT_SLOT + ':' + kaal[1];
        if (kaart[nieuweSleutel]) return;
        kaart[nieuweSleutel] = tam.krimpStuk(r.pixels, stuk) || r.pixels || '';
        ids[nieuweSleutel] = r.id;
        if (/[0-7]/.test(kaart[nieuweSleutel])) eigen[nieuweSleutel] = true;
      });
      this.beestjeIds = ids;
      // Een lege regel is geen tekening. Zonder dit onderscheid bleef het overzicht
      // "getekend" zeggen over een doekje dat je net had leeggemaakt.
      this.beestjeEigen = eigen;
      // De melding hoort te blijven staan als de nieuwe collectie er niet is; hij werd
      // hier eerst weer op false gezet en dan zag je nergens dat er iets ontbrak.
      this.setState({ beestjes: kaart, beestjesMist: !nieuweRijen, beestjesStatus: nieuweRijen ? 0 : fout }, () => {
        this.tamTeken();
        this.vulStukAls(this.state.beestjeAan);
      });
    } catch (e) {
      this.setState({ beestjesMist: true, beestjesStatus: 0 });
    }
  },

  /**
   * Een ander tabblad, en meteen het eerste stukje eruit.
   *
   * Anders sta je op het tabblad Objecten te kijken naar een lijf: het tabblad
   * bepaalt de lijst en het gekozen stukje bepaalt het doekje, en die twee horen
   * bij elkaar te blijven.
   */
  /**
   * Welke groepen stukjes er op een pagina staan.
   *
   * Het beestje en zijn gezicht staan sinds kort op één pagina, in twee banden. De
   * pagina heet nog steeds 'beestje', maar hij dekt er twee. Zie PAGINAS in
   * beestjeVals; dit is dezelfde lijst, klein gehouden zodat het vullen er ook bij kan.
   */
  paginaGroepen(id) {
    return id === 'beestje' ? ['beestje', 'gezicht'] : id === 'object' ? ['object'] : [];
  },

  /** Een stukje aanwijzen, zodat de knoppen eronder weten waar ze het over hebben. */
  kiesStuk(sleutel) {
    // Met stukVan en niet met stukOk: hier gaat het om de kale naam van het
    // stukje, en stukOk keurt de volledige sleutel met het beestjenummer ervoor.
    if (!tam.stukVan(sleutel)) return;
    this.setState({ beestjeAan: sleutel, beestjeFout: '' });
  },

  /**
   * Op welke rij van dit doekje de vloer begint.
   *
   * Het beestje staat op een vloer, en die ligt op de onderste rij van het veld. Valt
   * hij binnen je doekje, dan is alles vanaf daar geen tekengebied: wat je daar zet
   * ligt in of onder de vloer en zie je nooit terug. Vandaar dat die rijen op slot
   * zitten in plaats van dat je er tevergeefs in tekent.
   *
   * Geeft de hoogte terug als de vloer er niet bij staat, dus dan is er niets op slot.
   */
  vloerRijVan(sleutel) {
    const stuk = tam.stukVan(sleutel);
    if (!stuk) return Infinity;
    const fase = String(sleutel).startsWith('lijf-') ? sleutel.slice(5) : 'groot';
    const { stand } = this.standVoor(sleutel, fase);
    const anker = tam.ankersVan(stand, 0)[stuk.anker];
    if (!anker) return Infinity;
    const rij = (tam.VELD - 1) - (anker.y - Math.floor(stuk.h / 2));
    return rij < 0 ? 0 : rij;
  },

  /** Het doekje van één stukje, of een leeg vel. */
  doekVan(sleutel) {
    const stuk = tam.stukVan(sleutel);
    if (!stuk) return '';
    const nu = (this.state.beestjes || {})[this.stukKey(sleutel)];
    const leeg = tam.leegStuk(stuk);
    return typeof nu === 'string' && nu.length === leeg.length ? nu : leeg;
  },

  /** Het doekje waar de knoppen onderaan op werken. */
  huidigDoek() { return this.doekVan(this.state.beestjeAan); },

  /**
   * Eén vakje zetten.
   *
   * Meteen in de toestand, zonder op de server te wachten: tekenen moet direct
   * voelen. Bewaren doe je met de knop, zodat een uitgeschoten muis niet meteen
   * bij iedereen op het scherm staat.
   */
  zetStukVak(sleutel, i) {
    const doek = this.doekVan(sleutel);
    if (i < 0 || i >= doek.length) return;
    const stuk = tam.stukVan(sleutel);
    if (stuk && Math.floor(i / stuk.b) >= this.vloerRijVan(sleutel)) return;
    const teken = this.state.beestjeGum ? '.' : String(this.state.beestjeKleur || 0);
    if (doek[i] === teken) return;
    // Pas hier de stap onthouden, en niet bij het neerzetten van de muis: een klik
    // op een vakje dat die kleur al heeft verandert niets, en dan hoort er ook geen
    // stap terug bij te staan die niets doet.
    if (this.beestjeWacht) { this.beestjeWacht = false; this.beestjeStap(); }
    const na = doek.slice(0, i) + teken + doek.slice(i + 1);
    this.setState({
      beestjes: Object.assign({}, this.state.beestjes, { [this.stukKey(sleutel)]: na }),
      beestjeVuil: this.metVuil(sleutel)
    });
  },

  /**
   * Welke stukjes nog niet bewaard zijn.
   *
   * Een lijstje en niet een enkel jaknikkertje: op een pagina staan alle doekjes
   * open, dus je tekent een oog, een mond en een lijf voor je aan bewaren toe komt.
   * Eén knop schrijft ze daarna alle drie weg.
   */
  metVuil(sleutel) {
    return Object.assign({}, this.state.beestjeVuil, { [this.stukKey(sleutel)]: true });
  },

  /**
   * Een stap onthouden, zodat je hem ongedaan kunt maken.
   *
   * Eén stap is één handeling en niet één vakje: het moment dat je de muis neerzet,
   * niet elke pixel die je onderweg raakt. Anders klik je vijftig keer op terug om
   * één streep kwijt te raken.
   *
   * De hele kaart gaat mee en niet alleen het doekje. Dat is een paar honderd tekens
   * per stap, en daarmee klopt ook "leegmaken" en "terug naar de code" nog als je ze
   * ongedaan maakt.
   */
  beestjeStap() {
    const stapel = (this.beestjeTerugStapel || []).concat([this.state.beestjes || {}]);
    // Vijftig is ruim genoeg om een misser terug te draaien en weinig genoeg om
    // niet ongemerkt een halve megabyte tekenreeksen vast te houden.
    this.beestjeTerugStapel = stapel.slice(-50);
    // Een nieuwe streep maakt de vooruitstapel ongeldig: die tak bestaat niet meer.
    this.beestjeVooruitStapel = [];
    this.setState({ beestjeTerugKan: true, beestjeVooruitKan: false });
  },

  /**
   * Een momentopname terugzetten, heen of terug.
   *
   * Wat er onbewaard is wordt niet meegenomen uit de opname maar opnieuw uitgerekend:
   * alles wat door het terugzetten van waarde verandert is per definitie anders dan
   * wat er op de server staat. Zette ik het lijstje wél terug, dan verdween het
   * merkje zodra je tussendoor had bewaard, en dan stond er "Bewaard" onder een
   * doekje dat je net had teruggedraaid en kreeg je het er nooit meer op.
   */
  zetTerug(kaart) {
    const nu = this.state.beestjes || {};
    const vuil = Object.assign({}, this.state.beestjeVuil);
    Object.keys(Object.assign({}, nu, kaart)).forEach((k) => {
      if (nu[k] !== kaart[k]) vuil[k] = true;
    });
    return { beestjes: kaart, beestjeVuil: vuil };
  },

  /** Een stap terug. */
  stapTerug() {
    const stapel = this.beestjeTerugStapel || [];
    const vorige = stapel[stapel.length - 1];
    if (!vorige) return;
    this.beestjeTerugStapel = stapel.slice(0, -1);
    this.beestjeVooruitStapel = (this.beestjeVooruitStapel || []).concat([this.state.beestjes || {}]);
    this.setState(Object.assign(this.zetTerug(vorige), {
      beestjeTerugKan: !!this.beestjeTerugStapel.length, beestjeVooruitKan: true
    }), this.tamTeken);
    playBlip();
  },

  /** En weer vooruit. */
  stapVooruit() {
    const stapel = this.beestjeVooruitStapel || [];
    const volgende = stapel[stapel.length - 1];
    if (!volgende) return;
    this.beestjeVooruitStapel = stapel.slice(0, -1);
    this.beestjeTerugStapel = (this.beestjeTerugStapel || []).concat([this.state.beestjes || {}]);
    this.setState(Object.assign(this.zetTerug(volgende), {
      beestjeTerugKan: true, beestjeVooruitKan: !!this.beestjeVooruitStapel.length
    }), this.tamTeken);
    playBlip();
  },

  /**
   * Slepen over een doekje tekent door; je blijft op het doekje waar je begon.
   *
   * Met de emmer in de hand is één klik één vulling en gaat er niets mee met je
   * muis: slepen zou dan bij elk vakje opnieuw een heel vlak omkleuren.
   */
  stukDown(sleutel, i) {
    if (this.state.beestjeKies) { this.schaarDown(sleutel, i); return; }
    if (this.state.beestjeVul) {
      if (this.state.beestjeAan !== sleutel) this.setState({ beestjeAan: sleutel });
      this.vulStukVak(sleutel, i);
      return;
    }
    this.beestjeTekent = sleutel;
    this.beestjeWacht = true;
    if (this.state.beestjeAan !== sleutel) this.setState({ beestjeAan: sleutel });
    this.zetStukVak(sleutel, i);
  },

  /** Een vlak omkleuren vanaf het vakje waar je klikte. */
  vulStukVak(sleutel, i) {
    const stuk = tam.stukVan(sleutel);
    const doek = this.doekVan(sleutel);
    if (!stuk || !doek) return;
    const vloer = this.vloerRijVan(sleutel);
    if (Math.floor(i / stuk.b) >= vloer) return;
    const teken = this.state.beestjeGum ? '.' : String(this.state.beestjeKleur || 0);
    // De vloer blijft de vloer, ook als de vulling eronder door probeert te lopen.
    const gevuld = tam.vulStuk(doek, stuk, i, teken);
    const grens = Math.min(doek.length, Math.max(0, vloer) * stuk.b);
    const na = gevuld.slice(0, grens) + doek.slice(grens);
    // Klik je op een vakje dat die kleur al heeft, dan komt hetzelfde doekje terug
    // en hoort er ook geen stap terug bij te staan die niets doet.
    if (na === doek) return;
    this.beestjeStap();
    this.setState({
      beestjes: Object.assign({}, this.state.beestjes, { [this.stukKey(sleutel)]: na }),
      beestjeVuil: this.metVuil(sleutel)
    });
    playBlip();
  },
  stukOver(sleutel, i) {
    if (this.state.beestjeKies) { this.schaarOver(sleutel, i); return; }
    if (this.beestjeTekent === sleutel) this.zetStukVak(sleutel, i);
  },
  stukUp() { this.beestjeTekent = ''; this.beestjeWacht = false; this.beestjeSleep = null; },

  /** Het vakje waar je klikte, als x en y. */
  vakXY(sleutel, i) {
    const stuk = tam.stukVan(sleutel);
    return stuk ? { x: i % stuk.b, y: Math.floor(i / stuk.b) } : null;
  },

  /**
   * Neerzetten met de schaar: binnen de selectie oppakken, erbuiten een nieuwe trekken.
   */
  schaarDown(sleutel, i) {
    const plek = this.vakXY(sleutel, i);
    if (!plek) return;
    if (this.state.beestjeAan !== sleutel) this.setState({ beestjeAan: sleutel, beestjeSel: null });
    const sel = this.state.beestjeSel;
    const erin = sel && sel.sleutel === sleutel
      && plek.x >= sel.x0 && plek.x <= sel.x1 && plek.y >= sel.y0 && plek.y <= sel.y1;
    if (erin) {
      // Oppakken. De stap wordt nu vastgelegd, want het slepen dat volgt is één zet.
      this.beestjeStap();
      this.beestjeSleep = {
        sleutel, van: plek, rect: { x0: sel.x0, y0: sel.y0, x1: sel.x1, y1: sel.y1 },
        doek0: this.doekVan(sleutel)
      };
      return;
    }
    this.beestjeSleep = { sleutel, van: plek, trek: true };
    this.setState({ beestjeSel: { sleutel, x0: plek.x, y0: plek.y, x1: plek.x, y1: plek.y } });
  },

  /** Slepen met de schaar: de selectie groter maken of hem verschuiven. */
  schaarOver(sleutel, i) {
    const sleep = this.beestjeSleep;
    if (!sleep || sleep.sleutel !== sleutel) return;
    const plek = this.vakXY(sleutel, i);
    if (!plek) return;
    const vloer = this.vloerRijVan(sleutel);
    if (sleep.trek) {
      // De vloer valt buiten de selectie: daar valt niets op te pakken.
      const y1 = Math.min(Math.max(sleep.van.y, plek.y), Math.max(0, vloer - 1));
      this.setState({
        beestjeSel: {
          sleutel,
          x0: Math.min(sleep.van.x, plek.x), y0: Math.min(sleep.van.y, plek.y),
          x1: Math.max(sleep.van.x, plek.x), y1
        }
      });
      return;
    }
    const dx = plek.x - sleep.van.x;
    const dy = plek.y - sleep.van.y;
    const stuk = tam.stukVan(sleutel);
    const na = tam.verplaatsStuk(sleep.doek0, stuk, sleep.rect, dx, dy, vloer);
    this.setState({
      beestjes: Object.assign({}, this.state.beestjes, { [this.stukKey(sleutel)]: na }),
      beestjeVuil: this.metVuil(sleutel),
      beestjeSel: {
        sleutel,
        x0: sleep.rect.x0 + dx, y0: sleep.rect.y0 + dy,
        x1: sleep.rect.x1 + dx, y1: sleep.rect.y1 + dy
      }
    });
  },

  kiesStukKleur(k) { this.setState({ beestjeKleur: k, beestjeGum: false }); },
  /**
   * Drie gereedschappen: potlood, gum en emmer.
   *
   * Het potlood is "geen van beide", dus die knop zet de andere twee uit. De gum en
   * de emmer staan los van elkaar: samen zijn ze een vlakgum, en daarmee haal je in
   * één klik de lucht om een lijf weg. Op het tekendoek kan dat niet, maar daar heb
   * je ook geen doekje van tien bij zes.
   */
  pakStukPotlood() { this.setState({ beestjeGum: false, beestjeVul: false, beestjeKies: false, beestjeSel: null }); },
  pakStukGum() { this.setState({ beestjeGum: !this.state.beestjeGum, beestjeKies: false, beestjeSel: null }); },
  pakStukEmmer() { this.setState({ beestjeVul: !this.state.beestjeVul, beestjeKies: false, beestjeSel: null }); },
  /**
   * De schaar: een vlak aanwijzen en verslepen.
   *
   * Sluit de andere twee uit, want een gum of een emmer met een selectie erbij is
   * twee dingen tegelijk willen. De selectie zelf gaat weg als je hem neerlegt.
   */
  pakStukSchaar() {
    const aan = !this.state.beestjeKies;
    this.setState({ beestjeKies: aan, beestjeGum: false, beestjeVul: false, beestjeSel: null });
  },

  /** Het doekje leegmaken. */
  wisStuk() {
    const sleutel = this.state.beestjeAan;
    const stuk = tam.stukVan(sleutel);
    if (!stuk) return;
    const leeg = tam.leegStuk(stuk);
    if (this.doekVan(sleutel) === leeg) return;
    this.beestjeStap();
    this.setState({
      beestjes: Object.assign({}, this.state.beestjes, { [this.stukKey(sleutel)]: leeg }),
      beestjeVuil: this.metVuil(sleutel)
    });
    playBlip();
  },

  /**
   * Het doekje vullen met wat de code nu tekent.
   *
   * Zo begin je niet met een leeg vel maar met het beestje zoals het er nu uitziet,
   * en pas je aan wat je anders wil. Het knipt een venster van 16 bij 16 uit de
   * tekening, rond het ankerpunt van dit stukje.
   */
  neemHuidigeOver() {
    const sleutel = this.state.beestjeAan;
    const doek = this.standaardDoek(sleutel);
    if (!doek || doek === this.doekVan(sleutel)) return;
    this.beestjeStap();
    this.setState({
      beestjes: Object.assign({}, this.state.beestjes, { [this.stukKey(sleutel)]: doek }),
      beestjeVuil: this.metVuil(sleutel)
    });
    playBlip();
  },

  /**
   * Hetzelfde, maar dan als tekenreeks en zonder iets om te zetten.
   *
   * Apart, want dit is ook de startstand: open je een stukje dat nog nooit getekend
   * is, dan staat er meteen wat de code nu tekent in plaats van een leeg vel. Je
   * begint dus altijd met iets en past aan; een leeg raster van 256 vakjes is geen
   * beginpunt maar een opgave.
   */
  /**
   * De stand waarin dit stukje te zien is.
   *
   * Een mond voor een hongerig beestje hoort bij een hongerig beestje, en een Z'je
   * alleen bij een slapend. Zowel het doekje als het voorbeeldje ernaast rekenen
   * hiermee, want anders teken je in de ene stand en kijk je naar de andere.
   *
   * `anders` is de fase voor alles wat geen lijfje is: het doekje pakt de grootste,
   * want daar is de meeste ruimte, en het voorbeeldje een kind, want dat is waar je
   * naar wilt kijken.
   */
  standVoor(sleutel, anders) {
    const beest = this.state.tamBeest || tam.nieuwBeest(Date.now() - 6 * 24 * 3600e3);
    const fase = sleutel === 'ei' ? 'ei'
      : String(sleutel || '').startsWith('lijf-') ? sleutel.slice(5) : (anders || 'groot');
    const humeur = sleutel === 'mond-honger' ? 'hongerig'
      : sleutel === 'mond-verdrietig' ? 'verdrietig'
        : sleutel === 'wolkje' ? 'ziek'
          : sleutel === 'mond-slaap' || sleutel === 'ogen-dicht' || sleutel === 'zzz' ? 'slaapt' : 'blij';
    // Het hoopje slaapt mee: dan zoemt het vliegje niet, en houd je een schoon
    // hoopje over in plaats van een hoopje met een stip erboven.
    const slaapt = humeur === 'slaapt' || sleutel === 'hoopje';
    const actie = sleutel === 'appel' ? 'voeren' : sleutel === 'hartje' ? 'aaien'
      : sleutel === 'bal' ? 'spelen' : null;
    const stand = Object.assign({}, tam.standVan(beest, Date.now(), 12), {
      fase, humeur, slaapt,
      hoopjes: sleutel === 'hoopje' || sleutel === 'vlieg' ? 1 : 0
    });
    return { stand, actie };
  },

  stukUitCode(sleutel) {
    const stuk = tam.STUKKEN.find((s) => s.sleutel === sleutel);
    if (!stuk) return;
    const { stand, actie } = this.standVoor(sleutel, 'groot');
    const anker = tam.ankersVan(stand, 0)[stuk.anker] || { x: 12, y: 12 };
    // Het venster staat met zijn midden op het ankerpunt; bij een oneven maat valt
    // dat een halve pixel naar links, en dat is waar de code zelf ook rekent.
    const halfB = Math.floor(stuk.b / 2);
    const halfH = Math.floor(stuk.h / 2);
    const veld = new Array(stuk.b * stuk.h).fill('.');

    /**
     * Welke pixels bij dít stukje horen: één laag, niet het hele plaatje.
     *
     * Een lijf zonder gezicht en een gezicht zonder lijf, anders bak je het gezicht
     * in het lijf en tekent de app er nog een tweede overheen. En de objecten zitten
     * allemaal in de laag `extra`.
     *
     * Eerst haalde ik de objecten eruit door het beeld met en zonder actie te
     * vergelijken. Dat werkte voor het appeltje, het hartje en de bal, maar niet
     * voor het Z'je, het wolkje, het hoopje en het vliegje: die komen niet uit een
     * actie maar uit de stand, en dan trek je hetzelfde beeld van zichzelf af en
     * houd je niets over. Vandaar de laag.
     */
    const isLijf = sleutel === 'ei' || sleutel.startsWith('lijf-');
    const deel = isLijf ? 'lijf'
      : sleutel.startsWith('ogen') ? 'ogen'
        : sleutel.startsWith('mond') ? 'mond' : 'extra';
    let mijne = tam.pixels(stand, 0, actie, null, deel);
    if (sleutel === 'vlieg') {
      /**
       * Het vliegje hoort bij het hoopje en wordt in dezelfde lus getekend, dus het
       * hoopje ligt mee in beeld. Slapend zoemt er niets: wat er bij wakker wél is
       * en bij slapend niet, is precies het vliegje.
       */
      const stil = Object.assign({}, stand, { slaapt: true });
      const zonder = new Set(tam.pixels(stil, 0, actie, null, deel).map((q) => q.x + ',' + q.y));
      mijne = mijne.filter((q) => !zonder.has(q.x + ',' + q.y));
    }

    mijne.forEach((p) => {
      const x = p.x - (anker.x - halfB);
      const y = p.y - (anker.y - halfH);
      if (x < 0 || y < 0 || x >= stuk.b || y >= stuk.h) return;
      veld[y * stuk.b + x] = String(p.k);
    });
    return veld.join('');
  },

  /** De codeversie van een stukje als losse pixels, voor de tegeltjes. */
  stukPixelsUitCode(sleutel) {
    const stuk = tam.stukVan(sleutel);
    return tam.stukPixels(this.stukUitCode(sleutel), stuk) || [];
  },

  /**
   * Waar een doekje mee begint als er nog niets voor bewaard is.
   *
   * Dezelfde terugvalketen als in het spel (beeldVan in lib/tamagotchi.js): eerst de
   * standaard van jouw soort, dan die van soort één, dan de code. Hier stond alleen
   * soort één, en dan begon iemand met een beestje van soort twee op een ander lijf
   * dan hij in de Krabbelgotchi ziet; de knop "Standaard" zette hem ook daarop terug.
   */
  standaardDoek(sleutel) {
    const stuk = tam.stukVan(sleutel);
    if (!stuk) return null;
    const kaart = this.state.beestjes || {};
    const soort = tam.soortVanBeest(this.state.tamBeest || {});
    for (const wie of [soort, 1]) {
      const d = kaart[tam.stukSleutel(wie, sleutel)];
      if (typeof d === 'string' && d.length === stuk.b * stuk.h && /[0-7]/.test(d)) return d;
    }
    return this.stukUitCode(sleutel);
  },

  /**
   * Zorgen dat er iets op het doekje staat.
   *
   * Alleen als er nog niets bewaard is, en zonder het als wijziging te tellen: je
   * hebt niets veranderd, je kijkt naar wat er al was. Vandaar dat `beestjeVuil`
   * hier niet aan gaat en de bewaarknop uit blijft tot je echt een vakje zet.
   */
  vulStukAls (sleutel) { return this.vulGroep(null, sleutel); },

  /**
   * Alle lege doekjes van een pagina vullen met wat de code nu tekent.
   *
   * In één keer, want op een pagina staan ze allemaal open. Alleen wat nog niet
   * bewaard is; wat je zelf tekende blijft staan. En zonder het vuil te maken: je
   * hebt niets veranderd, je kijkt naar wat er al was.
   */
  vulGroep(groep, alleen) {
    const kaart = this.state.beestjes || {};
    const erbij = {};
    const groepen = this.paginaGroepen(groep);
    tam.STUKKEN.forEach((s) => {
      if (alleen ? s.sleutel !== alleen : groepen.indexOf(s.groep) < 0) return;
      const key = this.stukKey(s.sleutel);
      const nu = kaart[key];
      if (typeof nu === 'string' && nu.length === s.b * s.h) return;
      const doek = this.standaardDoek(s.sleutel);
      if (doek) erbij[key] = doek;
    });
    if (!Object.keys(erbij).length) return;
    /**
     * Ook in de stapel bijzetten, en niet als eigen stap.
     *
     * Dit is geen bewerking maar de startstand van een tabblad. Stond het er alleen
     * in de huidige kaart, dan sloeg één stap terug de doekjes van dat tabblad weer
     * leeg: de opname van daarvoor kende die sleutels nog niet.
     */
    const meterin = (o) => Object.assign({}, erbij, o);
    this.beestjeTerugStapel = (this.beestjeTerugStapel || []).map(meterin);
    this.beestjeVooruitStapel = (this.beestjeVooruitStapel || []).map(meterin);
    this.setState({ beestjes: Object.assign({}, kaart, erbij) });
  },

  /** Alles wat het tekenscherm nodig heeft. */
  beestjeVals() {
    const mob = this.state.isMobile;
    const aan = this.state.beestjeAan;
    const stuk = tam.stukVan(aan);
    const groep = this.state.beestjeGroep || 'beestje';

    /**
     * Eén vakjesmaat voor de hele pagina.
     *
     * Dat is het punt van naast elkaar zetten: het vliegje hoort kleiner te zijn
     * dan het lijf. Elk doekje apart passend maken deed precies het omgekeerde,
     * en dan teken je een appeltje op ware grootte van een beestje.
     *
     * De maat komt uit de breedte die er is, gedeeld door alle stukjes samen.
     * Hele pixels, anders wordt het raster onscherp.
     */
    /**
     * De breedte die er in het paarse vlak is.
     *
     * Het venster min tweemaal 24 lucht, het vlak daarbinnen min zijn rand van 3 en
     * zijn eigen 10 lucht aan weerszijden. Zo blijft er geen paars over waar niets
     * staat, en wordt het venster niet breder dan het nodig heeft.
     */
    const VENSTER = 860;
    const plaat = mob ? 300 : VENSTER - 48 - 8 - 6 - 20;
    const GAT = 12;
    /** Wat een doekje aan onderschrift en rand kost, los van de pixels zelf. */
    const KAP = 6 + 6 + 32;
    /**
     * Twee pagina's, en op de eerste twee banden onder elkaar.
     *
     * Het lijf en het gezicht horen bij elkaar: je tekent een oog en wilt zien wat
     * dat op zijn kop doet. Naast elkaar paste dat niet, want dan staan er zeven
     * kolommen op één regel en zakken de vakjes naar zes pixels. Onder elkaar wel,
     * en het leest ook beter: bovenin hoe hij groeit, daaronder hoe hij kijkt.
     */
    const PAGINAS = [
      {
        id: 'beestje',
        naam: 'Beestje',
        // Zonder kopjes erboven: de ene band is hoog en de andere plat, dus je ziet
        // zo al waar het lijf ophoudt en het gezicht begint.
        banden: [{ groep: 'beestje' }, { groep: 'gezicht' }]
      },
      { id: 'object', naam: 'Objecten', banden: [{ groep: 'object' }] }
    ];
    const stukkenVan = (band) => tam.STUKKEN.filter((x) => x.groep === band.groep);
    const paginaVan = (id) => PAGINAS.find((x) => x.id === id) || PAGINAS[0];
    /**
     * Eén vakjesmaat voor alle drie de pagina's.
     *
     * De krapste pagina bepaalt hem, en de andere twee volgen. Zou elke pagina zijn
     * eigen maat krijgen, dan waren de appeltjes groter dan de lijven zodra je op
     * dat tabblad klikte, en juist dat verschil wil je zien.
     */
    const alleBanden = PAGINAS.reduce((t, x) => t.concat(x.banden), []);
    const vak = Math.max(6, Math.min(22, alleBanden.reduce((m, band) => {
      const rij = stukkenVan(band);
      const som = rij.reduce((t, x) => t + x.b, 0) || 1;
      // De rand van 3 pixels aan weerszijden telt mee. Zonder die 6 per doekje paste
      // de band met de lijven net niet, wipte de langste naar een tweede regel en
      // viel zijn bovenkant boven het vel: onbereikbaar, want het vel centreert.
      return Math.min(m, Math.floor((plaat - GAT * (rij.length - 1) - 6 * rij.length) / som));
    }, 99)));
    /**
     * Het vel is even hoog op elk tabblad.
     *
     * Hoog genoeg voor de langste kolom van alle drie de pagina's. Anders krimpt het
     * venster onder je handen zodra je een tabblad verder klikt, en dan springen de
     * knoppen eronder mee.
     */
    /** Wat het vel zelf kost: zijn rand van 3, zijn eigen lucht en die van de rij. */
    const VEL_RAND = 6 + 20 + 8;
    const bandHoog = (band) => stukkenVan(band).reduce((h, x) => Math.max(h, x.h * vak + KAP), 0);
    /** Hoe breed een band wordt: de doekjes, hun randen en de gaten ertussen. */
    const bandBreed = (band) => {
      const rij = stukkenVan(band);
      return rij.reduce((t, x) => t + x.b * vak + 6, 0) + (rij.length - 1) * GAT;
    };
    /**
     * Alle banden van een pagina even breed, uitgelijnd op hun buitenranden.
     *
     * De onderste band met zes gezichtjes is van zichzelf breder dan de bovenste met
     * vier lijven, en dan staan ze allebei netjes gecentreerd maar niet onder elkaar.
     * De doekjes smaller maken kon niet: dan wordt de vakjesmaat per band anders en
     * klopt het grootteverschil tussen een mond en een lijf niet meer, en daar is deze
     * hele indeling om begonnen. Dus blijft de maat en gaat de ruimte in de gaten.
     */
    const paginaBreed = paginaVan(groep).banden.reduce((m, b) => Math.max(m, bandBreed(b)), 0);
    const velHoog = PAGINAS.reduce((m, pag) => Math.max(m, pag.banden
      .reduce((h, band) => h + bandHoog(band), 0) + (pag.banden.length - 1) * GAT), 0) + VEL_RAND;
    /** Het gat tussen de knopjes onder het vlak. */
    const GAT_KNOP = 7;
    /**
     * De breedte van de kolom: het venster min zijn lucht én zijn rand.
     *
     * Die rand van 4 aan weerszijden vergeten was genoeg om de emmer van de regel af
     * te laten vallen: elf vierkantjes passen dan net niet meer.
     */
    const KOLOM = VENSTER - 48 - 8;
    /**
     * De maat van alles wat vierkant hoort te blijven: kleur, gum, stap terug.
     *
     * Groeit mee tot de bovenste regel precies zo breed is als het paarse vlak en de
     * bewaarknop eronder. Elf vierkantjes en tien gaten vullen samen de kolom; wat er
     * door het afronden overblijft gaat in de gaten, want vierkant blijft vierkant.
     *
     * Op de telefoon 44, want dat is de ondergrens voor wat je met een vinger raakt,
     * en daar wipt de regel gewoon om. Zie sectie 6 van CLAUDE.md.
     */
    /**
     * Dertien vierkantjes op de bovenste regel: acht kleuren, drie gereedschappen en
     * de twee stapknoppen. De onderste regel is voor de twee brede knoppen.
     */
    const VIERKANTEN = 14;
    /** Het streepje tussen de kleuren en het gereedschap kost ook plek. */
    const STREEP = 3;
    const VIERKANT = mob ? 44
      : Math.max(34, Math.floor((KOLOM - STREEP - VIERKANTEN * GAT_KNOP) / VIERKANTEN));
    /** Het grijs van het palet, waar de vloer mee getekend wordt. */
    const K_GRIJS = 1;
    const sel = this.state.beestjeSel;
    /** Een gereedschapsknopje: vierkant, en paars als het aanstaat. */
    const gereedschap = (aan) => ({
      flex: '0 0 auto', width: VIERKANT + 'px', height: VIERKANT + 'px',
      padding: '0', borderRadius: '8px', justifyContent: 'center',
      background: aan ? 'var(--paars)' : '#fff',
      color: aan ? '#fff' : 'var(--ink)'
    });
    const slot = this.state.beestjeSlot || 0;
    // Jouw beestje: waar de tekenaar het over heeft. Zonder beestje (je hebt de
    // Krabbelgotchi nog nooit opengedaan) is er geen naam en geen soort, en dan valt
    // alles terug op "Jouw beestje" en soort één.
    const mijnBeest = this.state.tamBeest || null;
    const mijnSoort = tam.soortVanBeest(mijnBeest || {});
    // Hoeveel tegels het kiesscherm heeft: jouw beestje, en de spullenbak als je
    // tester of mod bent. Het venster en het raster rekenen er allebei mee.
    const aantalSlots = this.magBeta() ? 2 : 1;
    const vuile = Object.keys(this.state.beestjeVuil || {});
    /**
     * Een klein voorbeeld van een beestje, voor de zes tegels.
     *
     * Het lijf en het gezicht van dat beestje, zoals het er nu uitziet: getekend
     * waar getekend is en anders zoals de code hem tekent. Zo zie je in het
     * overzicht meteen welke van de zes je onder handen hebt gehad.
     */
    // Jouw beestje op de tegel, volgroeid en in een goed humeur: het tegeltje is een
    // spiegel en geen stand van zaken. Het soort bepaalt waar hij op terugvalt zolang
    // je zelf niets getekend hebt; zie de terugvalketen in beeldVan.
    const voorbeeld = (eigenaar, soort) => {
      const stand = Object.assign({}, tam.standVan(tam.nieuwBeest(Date.now() - 6 * 864e5, soort), Date.now(), 12),
        { fase: 'groot', humeur: 'blij', slaapt: false, hoopjes: 0 });
      // Vanaf nul geteld, net als de spullenbak, zodat het tegeltje ze allebei op
      // dezelfde manier kan neerzetten.
      return tam.beeldVan(stand, 0, null, this.state.beestjes || {}, soort, eigenaar)
        .filter((p) => p.y >= tam.KOP && p.y < tam.VELD - 1)
        .map((p) => ({ x: p.x, y: p.y - tam.KOP, k: p.k }));
    };

    /**
     * Het gezichtje op zijn plek op een lijf.
     *
     * Je tekent een lijf en je wilt weten waar de ogen en de mond terechtkomen; los
     * ernaast kijken en dan schatten werkt niet. Dit rekent hun ankerpunt om naar
     * vakjes van dít doekje, dus wat je ziet staat precies waar het in het spel komt
     * te staan.
     *
     * Doorschijnend, want het is niet van jou: op dit doekje teken je het lijf. Een
     * klik erdoorheen komt gewoon bij het lijf uit; zie `pointerEvents` in het scherm.
     */
    const schimVan = (fase) => {
      const lijf = tam.stukVan('lijf-' + fase);
      if (!lijf) return [];
      const { stand } = this.standVoor('lijf-' + fase, fase);
      const ankers = tam.ankersVan(stand, 0);
      const x0 = ankers.lijf.x - Math.floor(lijf.b / 2);
      const y0 = ankers.lijf.y - Math.floor(lijf.h / 2);
      const uit = [];
      ['ogen', 'mond-blij'].forEach((naam) => {
        const stuk = tam.stukVan(naam);
        const anker = stuk && ankers[stuk.anker];
        if (!anker) return;
        const ax = anker.x - Math.floor(stuk.b / 2);
        const ay = anker.y - Math.floor(stuk.h / 2);
        (tam.stukPixels(this.doekVan(naam), stuk) || []).forEach((q) => {
          uit.push({ x: ax + q.x - x0, y: ay + q.y - y0, k: q.k });
        });
      });
      return uit.filter((q) => q.x >= 0 && q.y >= 0 && q.x < lijf.b && q.y < lijf.h);
    };

    /**
     * De spullenbak op zijn tegeltje: alle objecten naast elkaar.
     *
     * Ze staan hier niet op hun ankerpunt maar netjes op een rij, want dit is een
     * inhoudsopgave en geen beestje. Wat getekend is komt van de server, de rest uit
     * de code, precies zoals in het spel zelf.
     */
    const spullenVoorbeeld = () => {
      const uit = [];
      let x = 0, y = 0, hoogste = 0;
      tam.STUKKEN.filter((s2) => s2.groep === 'object').forEach((s2) => {
        if (x + s2.b > tam.VELD) { x = 0; y += hoogste + 1; hoogste = 0; }
        const eigen = tam.stukPixels((this.state.beestjes || {})[tam.stukSleutel(1, s2.sleutel)], s2);
        const pix = eigen && eigen.length ? eigen : this.stukPixelsUitCode(s2.sleutel);
        pix.forEach((p) => uit.push({ x: x + p.x, y: y + p.y, k: p.k }));
        x += s2.b + 1;
        hoogste = Math.max(hoogste, s2.h);
      });
      return uit;
    };

    return {
      showBeestje: this.state.dialog === 'beestje',
      // ---- het overzicht: vijf beestjes en de spullenbak ----
      showBeestjeSlots: !slot,
      beestjeSlot: slot,
      naarSlots: this.naarSlots,
      /**
       * Twee tegels, en de tweede alleen voor de mod en de testers.
       *
       * Er waren zes: vijf soorten beestjes en de spullenbak. Die vijf waren van
       * iedereen samen — een oog bijwerken keek alle beestjes van dat soort anders
       * aan — en zijn hier weg. Wat er staat is jouw eigen beestje, met zijn eigen
       * naam erop, en dat raakt niemand anders.
       *
       * De vijf sleutels leven nog op de server en blijven de standaard waar een
       * beestje op terugvalt; ze zijn alleen niet meer bij te werken. Zie de
       * terugvalketen in beeldVan (lib/tamagotchi.js).
       *
       * De spullenbak blijft wel gedeeld, want een appeltje is een appeltje. Daarom
       * staat die nog achter het betavinkje: dat is geen tekening maar een besluit
       * voor iedereen.
       */
      beestjeSlots: [{ spullen: false }].concat(this.magBeta() ? [{ spullen: true }] : []).map(({ spullen }) => {
        const n = spullen ? tam.OBJECT_SLOT : 'eigen';
        const wie = spullen ? tam.OBJECT_SLOT : (this.state.accountId || '');
        const hoort = tam.STUKKEN.filter((y) => (y.groep === 'object') === spullen).length;
        // Wat er op de server staat, niet wat er in beeld staat: sinds een leeg doekje
        // gevuld wordt met de tekening van de code zou dat laatste altijd alles zijn,
        // ook voor een beestje waar je nooit aan hebt gezeten.
        const eigen = Object.keys(this.beestjeEigen || {}).filter((k) => k.indexOf(wie + ':') === 0).length;
        return {
          // Zijn naam als hij er een heeft. Zo niet, dan "Jouw beestje": dat zegt van
          // wie hij is, en een naam geven doe je in de Krabbelgotchi zelf.
          naam: spullen ? 'Objecten' : (tam.naamOk(mijnBeest && mijnBeest.naam) || 'Jouw beestje'),
          hoeveel: eigen ? eigen + ' van de ' + hoort + ' getekend' : 'nog niets getekend',
          onClick: () => this.kiesSlot(n),
          // De spullenbak laat zijn eigen spullen zien, een beestje zichzelf.
          pixels: spullen ? spullenVoorbeeld() : voorbeeld(wie, mijnSoort),
          vak: 6,
          tegelStyle: {
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
            padding: '10px', borderRadius: '16px', width: '100%', boxSizing: 'border-box',
            border: '3px solid var(--ink)', background: eigen ? 'var(--groen-vlak)' : 'var(--lila)'
          },
          naamStyle: { fontSize: '13px', fontWeight: 700 },
          hoeveelStyle: { fontSize: '11px', fontWeight: 600, color: 'var(--tekst-2)' }
        };
      }),
      /**
       * Zoveel kolommen als er tegels zijn, en niet drie.
       *
       * Er waren zes tegels en dus een raster van drie bij twee. Nu zijn het één of
       * twee, en dan stond er een venster ter breedte van drie tegels met twee tegels
       * erin: het gat rechts was even breed als de tegels zelf. Op de telefoon nog
       * steeds maximaal twee naast elkaar.
       */
      beestjeSlotRasterStyle: {
        display: 'grid',
        gridTemplateColumns: 'repeat(' + Math.min(mob ? 2 : 3, aantalSlots) + ', minmax(0,1fr))',
        // Kunnen scrollen, want op een laag scherm past de onderste rij er net niet
        // meer bij en dan was hij onbereikbaar: het venster zelf scrollt niet.
        gap: '10px', minWidth: '0', minHeight: '0', overflowY: 'auto',
        overscrollBehavior: 'contain', paddingRight: '2px'
      },
      /**
       * Het venster krimpt mee met wat erin staat.
       *
       * Twee maten: het kiesscherm is precies zo breed als zijn tegels, de tekenaar
       * houdt de 860 die hij nodig heeft voor de banden naast elkaar. Het stond op één
       * vaste maat in de view, en dan is het kiesscherm met twee tegels een lege zaal.
       *
       * De tegelbreedte volgt uit het doekje: `VELD * 6` met zijn eigen rand van drie,
       * plus tien padding en drie rand van de tegel aan elke kant. Daarbij het venster:
       * 24 padding en de rand van vier van `.gp-venster` aan elke kant, en de twee pixels
       * die het raster rechts vrijhoudt voor een scrollbalk. Die rand van het venster
       * en die twee stonden er niet in, en dan was de tegel 160 breed voor een doekje
       * dat er 170 nodig had: het stak vijf pixels in de padding aan elke kant.
       */
      beestjeVensterStyle: (() => {
        const tegel = tam.VELD * 6 + 2 * 3 + 2 * 10 + 2 * 3;
        const kolommen = Math.min(mob ? 2 : 3, aantalSlots);
        const kies = kolommen * tegel + (kolommen - 1) * 10 + 2 * 24 + 2 * 4 + 2;
        return {
          width: 'min(' + (slot ? 860 : kies) + 'px,100%)',
          maxHeight: 'min(92vh,880px)', padding: '24px',
          display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box'
        };
      })(),
      beestjeSlotDoekStyle: {
        position: 'relative', width: (tam.VELD * 6) + 'px', height: ((tam.VELD - tam.KOP - 1) * 6) + 'px',
        // De rand erbuiten en niet erbinnen: met border-box, dat de hele app gebruikt,
        // was de binnenkant maar 138 breed voor pixels die tot 144 lopen.
        boxSizing: 'content-box',
        border: '3px solid var(--ink)', borderRadius: '8px', background: '#fff', overflow: 'hidden'
      },
      beestjeTerugLabel: '‹ Terug',
      paletHex: PALETTE_HEX,
      openBeestje: this.openBeestje,
      sluitBeestje: this.sluitBeestje,
      beestjeTitel: !slot ? 'Beestje tekenen'
        : slot === tam.OBJECT_SLOT ? 'Objecten'
          : (tam.naamOk(mijnBeest && mijnBeest.naam) || 'Jouw beestje'),

      /**
       * Drie pagina's: het beestje zelf, zijn gezicht en zijn spulletjes.
       *
       * Alles van een pagina staat naast elkaar op dezelfde schaal, dus een vliegje
       * van vier bij vier is echt kleiner dan een lijf van achttien bij tweeëntwintig.
       * Dat is de hele reden voor deze indeling: bij één doekje per keer, elk keurig
       * passend gemaakt, tekende je een appeltje net zo groot als een beestje.
       */

      // De banden van deze pagina, allemaal met hetzelfde vakje.
      beestjeBanden: paginaVan(groep).banden.map((band) => ({
        // Onderaan uitlijnen, zodat alle doekjes van een band op dezelfde vloer staan.
        rijStyle: {
          display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end',
          justifyContent: stukkenVan(band).length > 1 ? 'space-between' : 'safe center',
          width: paginaBreed + 'px', maxWidth: '100%', margin: '0 auto',
          gap: '14px ' + GAT + 'px', minWidth: '0'
        },
        tegels: stukkenVan(band).map((s2) => {
        const doek = this.doekVan(s2.sleutel);
        const open = s2.sleutel === aan;
        const vuil = !!(this.state.beestjeVuil || {})[this.stukKey(s2.sleutel)];
        /**
         * Staat dit stukje op de server?
         *
         * Niet "staat er iets op het doekje": daar staat altijd iets, want een leeg
         * doekje wordt gevuld met wat de code nu tekent. De vraag is of jouw versie
         * bewaard is, en dat weet je alleen aan het regelnummer dat de server terug
         * gaf.
         */
        const bewaard = !!(this.beestjeEigen || {})[this.stukKey(s2.sleutel)];
        const vloerRij = this.vloerRijVan(s2.sleutel);
        return {
          naam: s2.naam,
          // De maat blijft in de tooltip staan; hij hoeft niet zeventien keer in beeld.
          titel: s2.naam + ', ' + s2.b + ' bij ' + s2.h + (vuil ? ', niet bewaard' : ''),
          onClick: () => this.kiesStuk(s2.sleutel),
          // Alleen op de lijven: op een ei zit geen gezicht.
          schim: s2.sleutel.startsWith('lijf-') ? schimVan(s2.sleutel.slice(5)) : [],
          schimVak: vak,
          // Het kader van de schaar, als het op dít doekje staat.
          toonSel: !!(sel && sel.sleutel === s2.sleutel),
          selStyle: sel && sel.sleutel === s2.sleutel ? {
            position: 'absolute', pointerEvents: 'none',
            left: (sel.x0 * vak) + 'px', top: (sel.y0 * vak) + 'px',
            width: ((sel.x1 - sel.x0 + 1) * vak) + 'px',
            height: ((sel.y1 - sel.y0 + 1) * vak) + 'px',
            border: '2px dashed var(--ink)', borderRadius: '2px',
            background: 'rgba(122,92,255,.14)', boxSizing: 'border-box'
          } : null,
          // De doekjes staan met hun onderkant op één lijn, want dan zie je het
          // hoogteverschil in één oogopslag.
          tegelStyle: {
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
            flex: '0 0 auto', minWidth: '0'
          },
          // Altijd dezelfde rand. Waar je in tekent zie je aan het onderschrift; een
          // gekleurd kadertje eromheen ging meetekenen met wat erin stond.
          /**
           * De rand staat om het raster heen, niet erover.
           *
           * Met `border-box` viel de rand binnen de opgegeven breedte, en dan bleef
           * er 6 pixels te weinig over voor de vakjes: de laatste kolom en de
           * onderste rij werden half afgesneden door de omlijning. Vandaar
           * `content-box`, zodat de binnenmaat precies b × vak is en de rand er
           * netjes omheen ligt.
           */
          doekStyle: {
            display: 'grid', boxSizing: 'content-box', position: 'relative',
            gridTemplateColumns: 'repeat(' + s2.b + ', ' + vak + 'px)',
            gridTemplateRows: 'repeat(' + s2.h + ', ' + vak + 'px)',
            width: (s2.b * vak) + 'px', height: (s2.h * vak) + 'px',
            border: '3px solid var(--ink)',
            borderRadius: '8px', overflow: 'hidden', background: '#fff',
            touchAction: 'none', userSelect: 'none'
          },
          kapStyle: {
            height: '32px', boxSizing: 'border-box', paddingTop: '4px',
            width: ((s2.b * vak) + 6) + 'px', overflow: 'hidden'
          },
          /**
           * De naam draagt nu ook de staat.
           *
           * De maat eronder is eruit: die stond er zeventien keer en zei je niets meer
           * zodra je het doekje ziet. Wat er wél toe doet, of het bewaard is, kleurt
           * de naam: paars is nog niet weg, groen staat op de server.
           */
          titelStyle: {
            fontSize: '11px', fontWeight: open || vuil ? 700 : 600, textAlign: 'center',
            lineHeight: '14px', wordBreak: 'break-word',
            color: vuil ? 'var(--paars)' : bewaard ? 'var(--groen-ink)' : 'var(--ink)'
          },
          /**
           * De vloer hoort erbij en is niet van jou.
           *
           * Het beestje staat erop, dus je wilt hem zien staan terwijl je een lijf
           * tekent. Maar wat je erin of eronder zet zie je nooit terug, dus die rijen
           * hebben geen tekenhandvatten: een klik erop doet niets.
           */
          vakken: doek.split('').map((c, i) => {
            const opDeVloer = Math.floor(i / s2.b) >= vloerRij;
            return {
              onDown: opDeVloer ? null : () => this.stukDown(s2.sleutel, i),
              onOver: opDeVloer ? null : () => this.stukOver(s2.sleutel, i),
              style: {
                width: vak + 'px', height: vak + 'px', boxSizing: 'border-box',
                background: opDeVloer ? PALETTE_HEX[K_GRIJS]
                  : c >= '0' && c <= '7' ? PALETTE_HEX[Number(c)] : 'transparent',
                cursor: opDeVloer ? 'not-allowed' : this.state.beestjeKies ? 'cell' : 'crosshair',
                // Geen streepje boven de vloer: het grijs zegt het zelf al, en een
                // extra lijn maakt er een tweede rand van binnen een rand.
                borderRight: opDeVloer ? 'none' : '1px solid rgba(36,27,58,.07)',
                borderBottom: opDeVloer ? 'none' : '1px solid rgba(36,27,58,.07)'
              }
            };
          })
          };
        })
      })),
      // De banden onder elkaar, met lucht ertussen.
      beestjeDoekRijStyle: {
        display: 'flex', flexDirection: 'column', justifyContent: 'safe center',
        gap: GAT + 'px', minWidth: '0'
      },
      // Welk stukje de knoppen onderaan aangaat.
      beestjeStukNaam: stuk ? stuk.naam : 'Kies een stukje',
      beestjeMaat: stuk ? stuk.b + ' × ' + stuk.h : '',
      beestjeMaatStyle: { fontSize: '11px', fontWeight: 700, color: 'var(--tekst-2)', fontVariantNumeric: 'tabular-nums' },
      beestjeKnopUit: !stuk,
      stukUp: this.stukUp,
      // Het palet: de acht kleuren van het doek, plus de gum.
      beestjeKleuren: PALETTE_HEX.map((hex, k) => ({
        onClick: () => this.kiesStukKleur(k),
        titel: 'Kleur ' + (k + 1),
        // Vierkant, en dat blijft zo: `flex: 0 0 auto` zodat de rij ernaast hem niet
        // platdrukt zodra de knoppen erbij op één regel moeten.
        style: {
          flex: '0 0 auto', width: VIERKANT + 'px', height: VIERKANT + 'px',
          padding: '0', borderRadius: '8px', background: hex,
          border: '3px solid ' + (!this.state.beestjeGum && this.state.beestjeKleur === k ? 'var(--paars)' : 'var(--ink)')
        }
      })),
      // Een stap terug en weer vooruit. Ook op Cmd+Z, net als bij de krabbel zelf;
      // zie handleKey in logic/lifecycle.js.
      stapTerug: this.stapTerug,
      stapVooruit: this.stapVooruit,
      beestjeTerugUit: !this.state.beestjeTerugKan,
      beestjeVooruitUit: !this.state.beestjeVooruitKan,
      beestjeStapStyle: {
        flex: '0 0 auto', width: VIERKANT + 'px', height: VIERKANT + 'px',
        padding: '0', borderRadius: '8px', justifyContent: 'center'
      },
      beestjeGum: !!this.state.beestjeGum,
      beestjeVul: !!this.state.beestjeVul,
      beestjePotlood: !this.state.beestjeGum && !this.state.beestjeVul,
      pakStukPotlood: this.pakStukPotlood,
      pakStukGum: this.pakStukGum,
      pakStukEmmer: this.pakStukEmmer,
      // Samen met de gum aan is het een vlakgum; vandaar dat de titel meebeweegt.
      beestjeEmmerTitel: this.state.beestjeGum ? 'Vlak leegmaken' : 'Vlak vullen',
      // De tekentjes staan op dezelfde maat en dikte als die van het tekendoek, want
      // het is hetzelfde gereedschap. Zie iconStroke in lib/icons.js.
      // Het tekentje schaalt mee met de knop, anders staat er een postzegel op een
      // tegel. De dikte volgt via iconStroke, zodat de lijn optisch gelijk blijft.
      beestjeIcoon: Math.round(VIERKANT * 0.5),
      beestjeIcoonLijn: iconStroke(Math.round(VIERKANT * 0.5)),
      beestjePotloodStyle: gereedschap(!this.state.beestjeGum && !this.state.beestjeVul && !this.state.beestjeKies),
      beestjeKies: !!this.state.beestjeKies,
      pakStukSchaar: this.pakStukSchaar,
      beestjeSchaarStyle: gereedschap(!!this.state.beestjeKies),
      beestjeEmmerStyle: gereedschap(!!this.state.beestjeVul),
      beestjeGumStyle: gereedschap(!!this.state.beestjeGum),
      /**
       * Alles op één regel: het palet, de gum, de stapknoppen en de twee knoppen.
       *
       * De vierkantjes houden hun maat en de twee brede knoppen vullen de rest op.
       * Past het niet, dan wipt er een stuk naar de volgende regel in plaats van dat
       * de kleuren rechthoekjes worden.
       */
      /**
       * De twee regels zijn even breed, en die breedte komt van de bovenste.
       *
       * Daar staan elf vierkantjes die hun maat houden, dus die regel is zo breed als
       * hij is. De onderste rekt zich anders uit tot de volle breedte van het paarse
       * vlak, en dan hangen er twee losse regels van verschillende lengte onder
       * elkaar. Nu schikken "Standaard" en "Leegmaken" in tot wat er over is.
       */
      /**
       * Twee regels, even breed, en die breedte komt van de bovenste.
       *
       * Daar staan acht kleuren en drie gereedschappen tegen elkaar aan, allemaal
       * vierkant, dus die regel is zo breed als hij is. De onderste rekt zich anders
       * uit tot de volle breedte en dan hangen er twee regels van verschillende lengte
       * onder elkaar; nu schikken "Standaard" en "Leegmaken" in tot wat er over is.
       */
      beestjePaletStyle: {
        display: 'flex', flexWrap: 'wrap', gap: GAT_KNOP + 'px', alignItems: 'center',
        justifyContent: 'space-between', minWidth: '0', width: '100%'
      },
      beestjeScheiStyle: {
        flex: '0 0 auto', width: STREEP + 'px', height: Math.round(VIERKANT * 0.7) + 'px',
        borderRadius: '999px', background: 'var(--rand-zacht)'
      },
      wisStuk: this.wisStuk,
      neemHuidigeOver: this.neemHuidigeOver,
      bewaarStuk: this.bewaarStuk,
      beestjeBezig: !!this.state.beestjeBezig,
      // Hoeveel er klaarstaan, zodat je niet hoeft te gokken of dat oog er ook in zit.
      beestjeBewaarLabel: this.state.beestjeBezig ? 'Opslaan…'
        : vuile.length ? 'Bewaren (' + vuile.length + ')' : 'Bewaard',
      beestjeBewaarUit: !!this.state.beestjeBezig || !vuile.length,
      /**
       * De collectie voor de ontwerpen ontbreekt.
       *
       * Deze stond eerst nergens in beeld, en dat is precies het soort stilte waar je
       * een half uur naar zoekt: je tekent, je bewaart, en er gebeurt niets.
       */
      showBeestjeMist: !!this.state.beestjesMist,
      beestjeMistTekst: this.state.beestjesStatus === 403
        ? 'De collectie ' + COL + ' staat op slot: zet List, View, Create en Update open.'
        : 'De collectie ' + COL + ' bestaat nog niet op de server, dus je tekening kan niet bewaard worden.',
      beestjeMistStyle: { fontSize: '12px', fontWeight: 600, color: 'var(--fout)', lineHeight: 1.4, textAlign: 'center' },
      showBeestjeFout: !!this.state.beestjeFout,
      beestjeFout: this.state.beestjeFout,
      beestjeFoutStyle: { fontSize: '12px', fontWeight: 600, color: 'var(--fout)', lineHeight: 1.4 },
      // Even hoog als de bewaarknop eronder: die is 48. Zie hoofdKnopStyle.
      beestjeKnopStyle: {
        flex: '1 1 0', minWidth: '0', height: '48px', justifyContent: 'center',
        borderRadius: '12px', fontSize: '15px', fontWeight: 600, whiteSpace: 'nowrap'
      },
      /**
       * Eén kolom: het paarse vlak met de doekjes, en daaronder de knoppen.
       *
       * `minHeight: 0` op elke laag: het venster heeft een plafond, en staat er ergens
       * in de keten een laag die niet inlevert, dan puilt de inhoud er onderlangs uit
       * in plaats van dat het vel kleiner wordt. Op een laag scherm stonden de knoppen
       * zo buiten beeld, en het venster scrollt zelf niet.
       */
      beestjeWerkStyle: {
        display: 'flex', flexDirection: 'column', gap: '12px',
        flex: '1 1 auto', minWidth: '0', minHeight: '0'
      },
      beestjeDoekVelStyle: {
        /**
         * Zo hoog als het moet, en kleiner als het niet anders kan.
         *
         * `0 1` en niet `0 0`: op een laag scherm loopt het venster tegen zijn plafond
         * en moet er íets inleveren. Zonder krimp was dat de onderkant van het vel,
         * buiten het venster, waar je niet bij kon. Nu krimpt het vel en scrollt het
         * vanbinnen. Bij genoeg ruimte is hij precies velHoog, en dus op elk tabblad
         * even hoog.
         */
        flex: '0 1 auto', height: velHoog + 'px', minHeight: '0', boxSizing: 'border-box',
        overflowY: 'auto', overscrollBehavior: 'contain',
        display: 'flex', flexDirection: 'column', justifyContent: 'safe center',
        border: '3px solid var(--ink)', borderRadius: '16px', background: 'var(--lila)',
        padding: '10px'
      }
    };
  },

  /** Hoeveel er nog in de oude lade liggen, zodat je weet wat er te verhuizen valt. */
  beestjeOudAantal() {
    const ids = this.beestjeIds || {};
    return Object.keys(this.beestjeEigen || {}).filter((k) => !ids[k]).length;
  },

  /**
   * Alles bewaren wat je getekend hebt.
   *
   * Eén regel per sleutel: bestaat hij al, dan wordt hij bijgewerkt, anders komt er
   * een bij. Na afloop lezen we terug wat de server ervan maakte, want een veld dat
   * er niet is wordt zonder klagen weggegooid.
   *
   * Achter elkaar en niet tegelijk: PocketBase krijgt anders zeventien verzoeken in
   * één adem, en de eerste die struikelt laat de rest in het duister staan.
   */
  async bewaarStuk() {
    let vuile = Object.keys(this.state.beestjeVuil || {}).filter(tam.stukOk);
    /**
     * Bewaren mag iedereen met een account; de spullenbak alleen de mod en de testers.
     *
     * Hier stond één poort op `magBeta()` voor alles. Nu is de eerste voorwaarde een
     * accountId, want daar staat je tekening onder, en blijft de betapoort alleen op
     * de objecten: die zijn van iedereen samen, dus dat is geen tekening maar een
     * besluit. De tegel is er ook niet zonder vinkje, maar een functie die alleen op
     * een verborgen tegel vertrouwt is geen slot.
     */
    if (!PB_URL || !this.state.accountId || !vuile.length || this.state.beestjeBezig) return;
    /**
     * Alleen wat van jou is: jouw eigen stukjes, en de spullenbak als je tester bent.
     *
     * Hier ging alles wat `stukOk` goedkeurde, en dat is ook de tekening van een
     * collega en de oude standaard onder `1:` tot `5:`. Die kwamen in de lijst via
     * terugzetten (Cmd+Z): dat merkt elke sleutel die verandert, en haalde `haalBeestjes`
     * tussendoor nieuwere tekeningen op, dan waren dat er ook van anderen. Bewaren
     * schreef dan hun oude pixels terug. Wat niet van jou is valt hier stil weg.
     */
    const mijn = this.state.accountId + ':';
    const magObjecten = this.magBeta();
    const vanMij = (k) => String(k).indexOf(mijn) === 0
      || (magObjecten && Number(String(k).split(':')[0]) === tam.OBJECT_SLOT);
    const nietVanMij = vuile.filter((k) => !vanMij(k));
    if (nietVanMij.length) {
      const over = Object.assign({}, this.state.beestjeVuil);
      nietVanMij.forEach((k) => { delete over[k]; });
      this.setState({ beestjeVuil: over });
    }
    vuile = vuile.filter(vanMij);
    if (!vuile.length) return;
    this.setState({ beestjeBezig: true, beestjeFout: '' });
    const kaart = this.state.beestjes || {};
    if (!this.beestjeIds) this.beestjeIds = {};
    // Wat er echt weggekomen is. Alleen dat verliest zijn merkje.
    const gelukt = [];
    /**
     * Het merkje weghalen bij wat bewaard is, en alleen daar.
     *
     * Uit de toestand van dít moment en niet uit een kopie van hiervoor: teken je
     * door terwijl het opslaan loopt, dan is dat merkje er inmiddels bij gekomen en
     * moet het blijven staan. Andersom ging het ook een keer mis: toen zette ik het
     * oude lijstje er weer overheen en bleef álles op "niet bewaard" staan, ook wat
     * netjes op de server was aangekomen.
     */
    const zonderGelukte = () => {
      const over = Object.assign({}, this.state.beestjeVuil);
      gelukt.forEach((k) => { delete over[k]; });
      return over;
    };
    try {
      for (const sleutel of vuile) {
        const pixels = kaart[sleutel] || '';
        const id = this.beestjeIds[sleutel];
        let res = id
          ? await api(`${PB_URL}/api/collections/${COL}/records/${id}`, asJson('PATCH', { sleutel, pixels }))
          : await api(`${PB_URL}/api/collections/${COL}/records`, asJson('POST', { sleutel, pixels }));
        /**
         * Een regel die er niet meer is: opnieuw aanmaken, één keer.
         *
         * Een 404 op een bestaande regel zegt "die is weg", niet "de collectie
         * bestaat niet". Dat gebeurt zodra iemand hem in PocketBase weggooit, en
         * dan bleef opslaan het voorgoed doen met een melding die je de verkeerde
         * kant op stuurde.
         */
        if (res.status === 404 && id) {
          delete this.beestjeIds[sleutel];
          res = await api(`${PB_URL}/api/collections/${COL}/records`, asJson('POST', { sleutel, pixels }));
        }
        if (!res.ok) throw new Error(String(res.status));
        const rec = await res.json();
        if ((rec.pixels || '') !== pixels) throw new Error('veld');
        if (rec.id) this.beestjeIds[sleutel] = rec.id;
        if (!this.beestjeEigen) this.beestjeEigen = {};
        if (/[0-7]/.test(pixels)) this.beestjeEigen[sleutel] = true;
        else delete this.beestjeEigen[sleutel];
        // Alleen als het doekje nog is wat we stuurden. Tekende je door terwijl dit
        // liep, dan staat er op de server een oudere versie en blijft het merkje.
        if (((this.state.beestjes || {})[sleutel] || '') === pixels) gelukt.push(sleutel);
      }
      this.setState({ beestjeBezig: false, beestjeVuil: zonderGelukte() });
      playPop();
    } catch (e) {
      this.setState({
        beestjeBezig: false,
        // Wat wel wegkwam blijft weg; wat bleef staan houdt zijn merkje, zodat je
        // het na een tweede poging niet opnieuw hoeft te tekenen.
        beestjeVuil: zonderGelukte(),
        beestjeFout: e.message === 'veld'
          ? 'Niet opgeslagen. De collectie ' + COL + ' heeft tekstvelden sleutel en pixels nodig.'
          : e.message === '404'
            ? 'De collectie ' + COL + ' bestaat nog niet op de server.'
            : 'Opslaan mislukt (' + e.message + ').'
      });
    }
  }
};
